import { saveAs } from 'file-saver';
import PizZip from 'pizzip';

/**
 * Interface for template data that can be injected into the Word template
 */
export interface TemplateData {
  title?: string;
  content?: string;
  tocContent?: string;
  clientName?: string;
  date?: string;
  author?: string;
  subtitle?: string;
  [key: string]: any; // Allow additional custom fields
}

/**
 * Configuration options for the template export
 */
export interface TemplateExportOptions {
  filename?: string;
  templatePath?: string;
}

/**
 * Error class for template export operations
 */
export class TemplateExportError extends Error {
  constructor(
    message: string,
    public details?: string
  ) {
    super(message);
    this.name = 'TemplateExportError';
  }
}

/**
 * Load template from public directory
 */
async function loadTemplate(templatePath: string): Promise<ArrayBuffer> {
  try {
    console.log(`📄 Loading template from: ${templatePath}`);
    const response = await fetch(templatePath);

    if (!response.ok) {
      throw new TemplateExportError(
        `Failed to load template: ${response.status} ${response.statusText}`,
        `Template path: ${templatePath}`
      );
    }

    const buffer = await response.arrayBuffer();
    console.log(`✅ Template loaded successfully (${buffer.byteLength} bytes)`);
    return buffer;
  } catch (error) {
    console.error('❌ Template loading failed:', error);
    if (error instanceof TemplateExportError) {
      throw error;
    }
    throw new TemplateExportError(
      'Unable to load document template',
      error instanceof Error ? error.message : 'Unknown error'
    );
  }
}

/**
 * Process TOC content to create Word XML with proper formatting
 */
function processTocContent(tocContent: string): string {
  try {
    if (!tocContent) return '';

    const lines = tocContent.split('\n');
    let tocXml = '';
    let processedItems = 1;

    lines.forEach((line) => {
      // Skip empty lines
      if (!line.trim()) return;

      // Extract level and text from the line
      let level = 0;
      let text = line.trim();
      let numberText = '';

      // Match different levels of TOC entries
      const mainMatch = text.match(/^(\d+)\.?\s+(.+)$/);
      const subMatch = text.match(/^\s*-?\s*(\d+)\.(\d+)\s+(.+)$/);
      const subSubMatch = text.match(/^\s*-?\s*(\d+)\.(\d+)\.(\d+)\s+(.+)$/);

      if (subSubMatch) {
        level = 2;
        numberText = `${subSubMatch[1]}.${subSubMatch[2]}.${subSubMatch[3]}`;
        text = subSubMatch[4].trim();
      } else if (subMatch) {
        level = 1;
        numberText = `${subMatch[1]}.${subMatch[2]}`;
        text = subMatch[3].trim();
      } else if (mainMatch) {
        level = 0;
        numberText = mainMatch[1];
        text = mainMatch[2].trim();
      } else {
        return; // Skip lines that don't match any pattern
      }

      // Add TOC entry with proper styling based on level
      tocXml += `
        <w:p>
          <w:pPr>
            <w:pStyle w:val="TOC${level + 1}"/>
            <w:tabs>
              <w:tab w:val="right" w:leader="none" w:pos="9350"/>
            </w:tabs>
            <w:ind w:left="${level * 360}"/>
          </w:pPr>
          <w:r>
            <w:rPr>
              <w:b w:val="${level === 0 ? 'on' : 'off'}"/>
              <w:sz w:val="${28 - level * 2}"/>
            </w:rPr>
            <w:t>${numberText}. ${text}</w:t>
          </w:r>
        </w:p>
      `;

      processedItems++;
    });

    return tocXml;
  } catch (error) {
    console.error('Error processing TOC content:', error);
    return '';
  }
}

/**
 * Convert markdown content to Word XML format
 */
function processMarkdownContent(markdown: string): string {
  let wordXml = '';

  // Split markdown into lines for processing
  const lines = markdown.split('\n');
  let inList = false;
  let listType = '';
  let listItems: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();

    // Skip empty lines
    if (!line) {
      if (inList) {
        // End current list if we encounter empty line
        wordXml += processListItems(listItems, listType);
        listItems = [];
        inList = false;
        listType = '';
      }
      continue;
    }

    // Check for headings (# style)
    const headingMatch = line.match(/^(#{1,6})\s+(.+)$/);
    if (headingMatch) {
      if (inList) {
        wordXml += processListItems(listItems, listType);
        listItems = [];
        inList = false;
        listType = '';
      }

      const level = headingMatch[1].length;
      const text = headingMatch[2];
      wordXml += `
        <w:p>
          <w:pPr>
            <w:pStyle w:val="Heading${level}"/>
            <w:keepNext/>
            ${
              level === 1
                ? `
            <w:pBdr>
              <w:bottom w:val="single" w:sz="4" w:space="1" w:color="ee8a81"/>
            </w:pBdr>
            `
                : ''
            }
          </w:pPr>
          <w:r>
            <w:rPr>
              <w:rFonts w:ascii="Montserrat" w:hAnsi="Montserrat"/>
              <w:sz w:val="${32 - (level - 1) * 2}"/>
              <w:b/>
              <w:color w:val="ee8a81"/>
            </w:rPr>
            <w:t>${text}</w:t>
          </w:r>
        </w:p>
      `;
      continue;
    }

    // Check for ordered list items (1. style)
    const orderedListMatch = line.match(/^(\d+)\.\s+(.+)$/);
    if (orderedListMatch) {
      const text = orderedListMatch[2];
      if (!inList || listType !== 'ordered') {
        if (inList) {
          wordXml += processListItems(listItems, listType);
          listItems = [];
        }
        inList = true;
        listType = 'ordered';
      }
      listItems.push(text);
      continue;
    }

    // Check for unordered list items (- or * style)
    const unorderedListMatch = line.match(/^[-*]\s+(.+)$/);
    if (unorderedListMatch) {
      const text = unorderedListMatch[1];
      if (!inList || listType !== 'unordered') {
        if (inList) {
          wordXml += processListItems(listItems, listType);
          listItems = [];
        }
        inList = true;
        listType = 'unordered';
      }
      listItems.push(text);
      continue;
    }

    // If we reach here, it's a regular paragraph
    if (inList) {
      wordXml += processListItems(listItems, listType);
      listItems = [];
      inList = false;
      listType = '';
    }

    // Process inline formatting
    const formattedText = processInlineFormatting(line);
    wordXml += `
      <w:p>
        <w:pPr>
          <w:pStyle w:val="Normal"/>
        </w:pPr>
        ${formattedText}
      </w:p>
    `;
  }

  // Process any remaining list items
  if (inList && listItems.length > 0) {
    wordXml += processListItems(listItems, listType);
  }

  return wordXml;
}

/**
 * Process list items into Word XML
 */
function processListItems(items: string[], type: string): string {
  let xml = '';
  items.forEach((item) => {
    const formattedText = processInlineFormatting(item);
    xml += `
      <w:p>
        <w:pPr>
          <w:pStyle w:val="ListParagraph"/>
          <w:ind w:left="360" w:hanging="360"/>
          <w:numPr>
            <w:ilvl w:val="0"/>
            <w:numId w:val="${type === 'ordered' ? '1' : '2'}"/>
          </w:numPr>
        </w:pPr>
        ${formattedText}
      </w:p>
    `;
  });
  return xml;
}

/**
 * Process inline markdown formatting to Word XML
 */
function processInlineFormatting(text: string): string {
  let xml = '';
  let currentText = '';
  let isBold = false;
  let isItalic = false;

  const flushCurrentText = () => {
    if (currentText) {
      xml += `
        <w:r>
          <w:rPr>
            <w:rFonts w:ascii="Montserrat" w:hAnsi="Montserrat"/>
            ${isBold ? '<w:b/>' : ''}
            ${isItalic ? '<w:i/>' : ''}
            <w:sz w:val="24"/>
          </w:rPr>
          <w:t>${currentText}</w:t>
        </w:r>
      `;
      currentText = '';
    }
  };

  // Process text character by character
  for (let i = 0; i < text.length; i++) {
    if (text[i] === '*' || text[i] === '_') {
      const isDouble = text[i + 1] === text[i];

      if (isDouble) {
        flushCurrentText();
        isBold = !isBold;
        i++; // Skip next character
      } else {
        flushCurrentText();
        isItalic = !isItalic;
      }
    } else {
      currentText += text[i];
    }
  }

  // Flush any remaining text
  flushCurrentText();

  return xml;
}

/**
 * Generate dynamic title content with responsive font sizing
 */
export function generateDynamicTitleContent(
  title: string,
  subtitle?: string
): string {
  // Calculate title length and determine font size
  const titleLength = title.length;
  let fontSize: number;
  let lineSpacing: number;
  let beforeSpacing: number;
  let afterSpacing: number;
  console.log('titleLength', titleLength);
  // Dynamic font sizing based on title length
  if (titleLength <= 100) {
    fontSize = 48; // Large font for short titles
    lineSpacing = 240;
    beforeSpacing = 4800;
    afterSpacing = 240;
  } else if (titleLength <= 120) {
    fontSize = 40; // Medium-large font
    lineSpacing = 200;
    beforeSpacing = 4600;
    afterSpacing = 200;
  } else if (titleLength <= 140) {
    fontSize = 32; // Medium font
    lineSpacing = 180;
    beforeSpacing = 4400;
    afterSpacing = 180;
  } else if (titleLength <= 160) {
    fontSize = 28; // Medium-small font
    lineSpacing = 160;
    beforeSpacing = 4200;
    afterSpacing = 160;
  } else if (titleLength <= 180) {
    fontSize = 24; // Small font
    lineSpacing = 140;
    beforeSpacing = 4000;
    afterSpacing = 140;
  } else {
    fontSize = 20; // Very small font for very long titles
    lineSpacing = 120;
    beforeSpacing = 3800;
    afterSpacing = 120;
  }

  // Break long titles into multiple lines if needed
  const maxWordsPerLine =
    titleLength > 60
      ? Math.ceil(title.split(' ').length / 2)
      : title.split(' ').length;
  const words = title.split(' ');
  const titleLines: string[] = [];

  if (words.length > maxWordsPerLine && titleLength > 60) {
    // Split into multiple lines for very long titles
    const wordsPerLine = Math.ceil(words.length / 2);
    for (let i = 0; i < words.length; i += wordsPerLine) {
      titleLines.push(words.slice(i, i + wordsPerLine).join(' '));
    }
  } else {
    titleLines.push(title);
  }

  // Generate XML for title lines
  const titleXml = titleLines
    .map(
      (line, index) => `
    <w:p>
      <w:pPr>
        <w:spacing w:before="${index === 0 ? beforeSpacing : lineSpacing}" w:after="${index === titleLines.length - 1 ? afterSpacing : lineSpacing}"/>
        <w:jc w:val="center"/>
      </w:pPr>
      <w:r>
        <w:rPr>
          <w:rFonts w:ascii="Montserrat" w:hAnsi="Montserrat"/>
          <w:sz w:val="${fontSize}"/>
          <w:b/>
          <w:color w:val="ee8a81"/>
        </w:rPr>
        <w:t>${line.toUpperCase()}</w:t>
      </w:r>
    </w:p>
  `
    )
    .join('');

  // Generate complete title content
  return `
    ${titleXml}
    ${
      subtitle
        ? `
    <w:p>
      <w:pPr>
        <w:spacing w:before="240" w:after="240"/>
        <w:jc w:val="center"/>
      </w:pPr>
      <w:r>
        <w:rPr>
          <w:rFonts w:ascii="Montserrat" w:hAnsi="Montserrat"/>
          <w:sz w:val="32"/>
          <w:b/>
          <w:color w:val="808080"/>
        </w:rPr>
        <w:t>${subtitle}</w:t>
      </w:r>
    </w:p>
    `
        : ''
    }
    <w:p>
      <w:pPr>
        <w:spacing w:before="240" w:after="480"/>
        <w:jc w:val="center"/>
      </w:pPr>
      <w:r>
        <w:rPr>
          <w:rFonts w:ascii="Montserrat" w:hAnsi="Montserrat"/>
          <w:sz w:val="24"/>
          <w:color w:val="999999"/>
        </w:rPr>
        <w:t>HEILPÄDAGOGISCHE TAGESGRUPPE</w:t>
      </w:r>
    </w:p>
    <w:p>
      <w:pPr>
        <w:spacing w:before="120" w:after="120"/>
        <w:jc w:val="center"/>
      </w:pPr>
    </w:p>
  `;
}

/**
 * Add content to template with proper structure and styling
 */
async function addContentToTemplate(
  templateBuffer: ArrayBuffer,
  data: TemplateData
): Promise<Uint8Array> {
  try {
    console.log('📝 Adding content to template');

    // Load the main content template ZIP (this is the working base)
    const zip = new PizZip(templateBuffer);

    // Read the main document content and styles
    const docXml = zip.file('word/document.xml')?.asText();
    const stylesXml = zip.file('word/styles.xml')?.asText();

    if (!docXml) {
      throw new TemplateExportError('Invalid template: document.xml not found');
    }

    console.log('📄 Template document loaded successfully');

    // Extract template's section properties
    const sectionMatch = docXml.match(/<w:sectPr[^>]*>[\s\S]*?<\/w:sectPr>/);
    if (!sectionMatch) {
      throw new TemplateExportError(
        'Invalid template: section properties not found'
      );
    }
    const templateSectPr = sectionMatch[0];

    // Create section properties for continuous sections
    const baseSectPr = templateSectPr
      .replace(/<\/w:sectPr>/, '')
      .replace(/<w:sectPr[^>]*>/, '')
      .replace(/<w:type[^>]*>/, '')
      .replace(/<w:pgNumType[^>]*>/, '');

    // Add required styles to the styles.xml
    if (stylesXml) {
      const updatedStylesXml = addRequiredStyles(stylesXml);
      zip.file('word/styles.xml', updatedStylesXml);
    }

    // Section break for TOC
    const tocSectionBreak = `
      <w:p>
        <w:pPr>
          <w:pStyle w:val="TOCHeading"/>
          <w:jc w:val="left"/>
          <w:spacing w:before="240" w:after="240"/>
        </w:pPr>
        <w:r>
          <w:t>Inhaltsverzeichnis</w:t>
        </w:r>
      </w:p>
    `;

    // Process TOC content
    const tocXml = data.tocContent ? processTocContent(data.tocContent) : '';

    // Process main content
    const contentXml = data.content ? processMarkdownContent(data.content) : '';

    // Build content structure with title on letterhead page and rest with page breaks
    console.log(
      '🔍 Building content with title on letterhead page and proper page breaks'
    );

    let modifiedDocXml = docXml;

    // 1. Insert title content right at the beginning (on letterhead page - page 0)
    if (data.title) {
      const titleContent = generateDynamicTitleContent(
        data.title,
        data.subtitle
      );

      // Insert title content right after <w:body> tag (on the letterhead page)
      const bodyStartMatch = modifiedDocXml.match(/<w:body[^>]*>/);
      if (bodyStartMatch) {
        const bodyStartEnd =
          modifiedDocXml.indexOf(bodyStartMatch[0]) + bodyStartMatch[0].length;
        modifiedDocXml =
          modifiedDocXml.slice(0, bodyStartEnd) +
          titleContent +
          modifiedDocXml.slice(bodyStartEnd);
        console.log(
          '✅ Title content inserted on letterhead page (page 0) with page break'
        );
      }
    }

    // 2. Build TOC and content for remaining pages
    let remainingContent = '';

    // Add TOC content (goes on page 1)
    if (tocXml) {
      remainingContent += `
        ${tocSectionBreak}
        ${tocXml}
      `;

      // Add page break after TOC to move to next page for main content
      remainingContent += `
        <w:p>
          <w:pPr>
            <w:pBdr>
              <w:top w:val="none"/>
              <w:left w:val="none"/>
              <w:bottom w:val="none"/>
              <w:right w:val="none"/>
            </w:pBdr>
          </w:pPr>
          <w:r>
            <w:br w:type="page"/>
          </w:r>
        </w:p>
      `;

      console.log('✅ TOC content added with page break');
    }

    // Add main content (goes on page 2)
    if (contentXml) {
      remainingContent += `${contentXml}`;
      console.log('✅ Main content added');
    }

    // Insert remaining content before closing body tag
    if (remainingContent) {
      const bodyEndIndex = modifiedDocXml.indexOf('</w:body>');
      modifiedDocXml =
        modifiedDocXml.slice(0, bodyEndIndex) +
        remainingContent +
        modifiedDocXml.slice(bodyEndIndex);
      console.log('✅ TOC and content inserted into template');
    }

    console.log('🔍 Final document XML length:', modifiedDocXml.length);
    console.log('✅ Document content merged successfully');

    // Update the document.xml in the ZIP
    zip.file('word/document.xml', modifiedDocXml);

    // Generate the final document
    const output = zip.generate({ type: 'uint8array' });
    console.log('✅ Template document created successfully');
    return output;
  } catch (error) {
    console.error('❌ Content template creation failed:', error);
    if (error instanceof TemplateExportError) {
      throw error;
    }
    throw new TemplateExportError(
      'Failed to create document with content',
      error instanceof Error ? error.message : 'Unknown error'
    );
  }
}

/**
 * Add required styles to the Word document
 */
function addRequiredStyles(stylesXml: string): string {
  // Find the closing tag of w:styles
  const insertPoint = stylesXml.lastIndexOf('</w:styles>');
  if (insertPoint === -1) return stylesXml;

  // Add our custom styles
  const customStyles = `
    <w:style w:type="paragraph" w:styleId="TOCHeading">
      <w:name w:val="TOC Heading"/>
      <w:basedOn w:val="Heading1"/>
      <w:next w:val="Normal"/>
      <w:pPr>
        <w:keepNext/>
        <w:spacing w:before="480" w:after="240"/>
        <w:jc w:val="center"/>
      </w:pPr>
      <w:rPr>
        <w:rFonts w:ascii="Montserrat" w:hAnsi="Montserrat"/>
        <w:sz w:val="32"/>
        <w:b/>
        <w:color w:val="ee8a81"/>
      </w:rPr>
    </w:style>

    <w:style w:type="paragraph" w:styleId="Heading1">
      <w:name w:val="Heading 1"/>
      <w:next w:val="Normal"/>
      <w:pPr>
        <w:spacing w:before="400" w:after="200"/>
        <w:keepNext/>
        <w:pBdr>
          <w:bottom w:val="single" w:sz="4" w:space="1" w:color="ee8a81"/>
        </w:pBdr>
      </w:pPr>
      <w:rPr>
        <w:rFonts w:ascii="Montserrat" w:hAnsi="Montserrat"/>
        <w:sz w:val="36"/>
        <w:b/>
        <w:color w:val="ee8a81"/>
      </w:rPr>
    </w:style>

    <w:style w:type="paragraph" w:styleId="Heading2">
      <w:name w:val="Heading 2"/>
      <w:next w:val="Normal"/>
      <w:pPr>
        <w:spacing w:before="300" w:after="150"/>
        <w:keepNext/>
      </w:pPr>
      <w:rPr>
        <w:rFonts w:ascii="Montserrat" w:hAnsi="Montserrat"/>
        <w:sz w:val="32"/>
        <w:b/>
        <w:color w:val="ee8a81"/>
      </w:rPr>
    </w:style>

    <w:style w:type="paragraph" w:styleId="Heading3">
      <w:name w:val="Heading 3"/>
      <w:next w:val="Normal"/>
      <w:pPr>
        <w:spacing w:before="200" w:after="100"/>
        <w:keepNext/>
      </w:pPr>
      <w:rPr>
        <w:rFonts w:ascii="Montserrat" w:hAnsi="Montserrat"/>
        <w:sz w:val="28"/>
        <w:b/>
        <w:color w:val="ee8a81"/>
      </w:rPr>
    </w:style>

    <w:style w:type="paragraph" w:styleId="Heading4">
      <w:name w:val="Heading 4"/>
      <w:next w:val="Normal"/>
      <w:pPr>
        <w:spacing w:before="160" w:after="80"/>
        <w:keepNext/>
      </w:pPr>
      <w:rPr>
        <w:rFonts w:ascii="Montserrat" w:hAnsi="Montserrat"/>
        <w:sz w:val="24"/>
        <w:b/>
        <w:i/>
        <w:color w:val="ee8a81"/>
      </w:rPr>
    </w:style>

    <w:style w:type="paragraph" w:styleId="Normal">
      <w:name w:val="Normal"/>
      <w:pPr>
        <w:spacing w:after="160" w:line="360" w:lineRule="auto"/>
        <w:jc w:val="both"/>
      </w:pPr>
      <w:rPr>
        <w:rFonts w:ascii="Montserrat" w:hAnsi="Montserrat"/>
        <w:sz w:val="24"/>
      </w:rPr>
    </w:style>

    <w:style w:type="paragraph" w:styleId="ListParagraph">
      <w:name w:val="List Paragraph"/>
      <w:basedOn w:val="Normal"/>
      <w:pPr>
        <w:spacing w:after="120"/>
        <w:ind w:left="360" w:hanging="360"/>
      </w:pPr>
      <w:rPr>
        <w:rFonts w:ascii="Montserrat" w:hAnsi="Montserrat"/>
        <w:sz w:val="24"/>
      </w:rPr>
    </w:style>

    <w:style w:type="numbering" w:styleId="OrderedList">
      <w:name w:val="Ordered List"/>
      <w:pPr>
        <w:ind w:left="360" w:hanging="360"/>
      </w:pPr>
      <w:rPr>
        <w:rFonts w:ascii="Montserrat" w:hAnsi="Montserrat"/>
        <w:sz w:val="24"/>
      </w:rPr>
    </w:style>

    <w:style w:type="numbering" w:styleId="UnorderedList">
      <w:name w:val="Unordered List"/>
      <w:pPr>
        <w:ind w:left="360" w:hanging="360"/>
      </w:pPr>
      <w:rPr>
        <w:rFonts w:ascii="Montserrat" w:hAnsi="Montserrat"/>
        <w:sz w:val="24"/>
      </w:rPr>
    </w:style>
  `;

  // Insert custom styles before the closing tag
  return (
    stylesXml.slice(0, insertPoint) +
    customStyles +
    stylesXml.slice(insertPoint)
  );
}

/**
 * Generate filename for the exported document
 */
function generateFilename(title?: string, customFilename?: string): string {
  if (customFilename) {
    return customFilename.endsWith('.docx')
      ? customFilename
      : `${customFilename}.docx`;
  }

  if (title) {
    const cleanTitle = title
      .replace(/[^a-zA-Z0-9\s]/g, '')
      .replace(/\s+/g, '-')
      .toLowerCase();
    return `${cleanTitle}-template.docx`;
  }

  return `document-template-${Date.now()}.docx`;
}

/**
 * Main function to generate Word document with template using real content
 */
export async function generateWordWithTemplate(
  data: TemplateData,
  options: TemplateExportOptions = {}
): Promise<void> {
  try {
    console.log('🚀 Starting template-based Word export');
    console.log('📊 Export data:', {
      title: data.title,
      hasContent: !!data.content,
      hasToc: !!data.tocContent,
      contentLength: data.content?.length || 0,
      tocLength: data.tocContent?.length || 0,
    });

    // Validate required data
    if (!data.title) {
      throw new TemplateExportError('Document title is required');
    }

    // Load the template
    const templatePath =
      options.templatePath || '/letter-head/letterhead-template.docx';
    const templateBuffer = await loadTemplate(templatePath);

    // Add content to template
    const documentBuffer = await addContentToTemplate(templateBuffer, data);

    // Generate filename and save
    const filename = generateFilename(data.title, options.filename);
    const blob = new Blob([documentBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    });

    console.log(`💾 Saving document: ${filename} (${blob.size} bytes)`);
    saveAs(blob, filename);

    console.log('✅ Template-based Word export completed successfully');
  } catch (error) {
    console.error('❌ Template-based Word export failed:', error);
    if (error instanceof TemplateExportError) {
      throw error;
    }
    throw new TemplateExportError(
      'Export failed',
      error instanceof Error ? error.message : 'Unknown error'
    );
  }
}

/**
 * Prepare template data from markdown content and metadata
 */
export function prepareTemplateData(
  markdownContent: string,
  tocContent?: string,
  additionalData: Partial<TemplateData> = {}
): TemplateData {
  return {
    content: markdownContent,
    tocContent: tocContent || '',
    title: additionalData.title || 'Document',
    subtitle: additionalData.subtitle || 'Konzeption',
    author: additionalData.author || '',
    clientName: additionalData.clientName || '',
    date: additionalData.date || new Date().toLocaleDateString('de-DE'),
    ...additionalData,
  };
}

/**
 * Validate template data has required fields
 */
export function validateTemplateData(
  data: TemplateData,
  requiredFields: string[] = ['title']
): void {
  for (const field of requiredFields) {
    if (!data[field]) {
      throw new TemplateExportError(
        `Required field '${field}' is missing from template data`
      );
    }
  }
}

// Debug functions for testing
export async function analyzeTemplate(
  templatePath: string = '/letter-head/letterhead-template.docx'
): Promise<void> {
  try {
    console.log('🔍 Analyzing letterhead template structure...');
    const buffer = await loadTemplate(templatePath);
    const zip = new PizZip(buffer);

    console.log('📁 Template files:', Object.keys(zip.files));

    const docXml = zip.file('word/document.xml')?.asText();
    if (docXml) {
      console.log('📄 Document structure found');
      console.log('📄 Document XML length:', docXml.length);
      console.log('📄 First 500 chars:', docXml.substring(0, 500));

      // Find section breaks
      const sectionMatches = docXml.match(
        /<w:sectPr[^>]*>[\s\S]*?<\/w:sectPr>/g
      );
      console.log('📄 Found', sectionMatches?.length || 0, 'section breaks');

      // Find paragraph breaks with section properties
      const paragraphSectionMatches = docXml.match(
        /<w:p[^>]*>\s*<w:pPr[^>]*>\s*<w:sectPr[^>]*>[\s\S]*?<\/w:sectPr>\s*<\/w:pPr>\s*<\/w:p>/g
      );
      console.log(
        '📄 Found',
        paragraphSectionMatches?.length || 0,
        'paragraph section breaks'
      );

      if (paragraphSectionMatches) {
        paragraphSectionMatches.forEach((match, index) => {
          console.log(
            `📄 Section break ${index + 1}:`,
            match.substring(0, 100) + '...'
          );
        });
      }

      console.log('✅ Template analysis complete');
    }
  } catch (error) {
    console.error('❌ Template analysis failed:', error);
  }
}

export async function testTemplate(): Promise<void> {
  try {
    console.log('🧪 Testing template...');

    const testData = prepareTemplateData(
      '<h1>Test Heading 1</h1><p>This is a test paragraph with some content.</p><h2>Sub Heading</h2><p>Another paragraph of test content.</p>',
      '1. Introduction\n2. Main Content\n2.1 Sub Section\n3. Conclusion',
      {
        title: 'Test Document Title',
        subtitle: 'Test Konzeption',
        author: 'Test Author',
      }
    );

    await generateWordWithTemplate(testData, {
      filename: 'template-test.docx',
    });

    console.log('✅ Template test completed successfully');
  } catch (error) {
    console.error('❌ Template test failed:', error);
  }
}

/**
 * Debug function to analyze title template
 */
export async function analyzeTitleTemplate(): Promise<void> {
  try {
    console.log('🔍 Analyzing title template structure...');
    const titleBuffer = await loadTemplate('/letter-head/title-template.docx');
    const titleZip = new PizZip(titleBuffer);
    const titleDocXml = titleZip.file('word/document.xml')?.asText();

    if (titleDocXml) {
      const titleBodyMatch = titleDocXml.match(
        /<w:body[^>]*>([\s\S]*?)<\/w:body>/
      );
      if (titleBodyMatch) {
        const titleContent = titleBodyMatch[1].trim();
        console.log('📄 Title template content length:', titleContent.length);
        console.log(
          '📄 Title template content preview:',
          titleContent.substring(0, 200)
        );
      }
    }
    console.log('✅ Title template analysis completed');
  } catch (error) {
    console.error('❌ Title template analysis failed:', error);
  }
}

/**
 * Test function to verify dynamic title sizing with different lengths
 */
export async function testDynamicTitleSizing(): Promise<void> {
  try {
    console.log('🧪 Testing dynamic title sizing...');

    const testTitles = [
      'Short Title',
      'Medium Length Title That Should Scale',
      'This is a Very Long Title That Should Be Scaled Down and Potentially Split Into Multiple Lines',
      'This is an Extremely Long Title That Should Be Scaled Down to a Very Small Font Size and Definitely Split Into Multiple Lines to Fit Properly on the Page Without Overlapping or Going Off the Edge',
      'ANOTHER EXTREMELY LONG TITLE WITH MANY WORDS THAT NEEDS TO BE HANDLED GRACEFULLY WITH SMALLER FONT SIZES AND PROPER LINE BREAKS',
    ];

    for (const title of testTitles) {
      console.log(`\n📝 Testing title: "${title}"`);
      console.log(`   Length: ${title.length} characters`);

      const testData = prepareTemplateData(
        '<p>Test content</p>',
        '1. Test TOC',
        {
          title: title,
          subtitle: 'Test Konzeption',
          author: 'Test Author',
        }
      );

      const filename = `title-test-${title.length}-chars.docx`;
      await generateWordWithTemplate(testData, { filename });

      console.log(`   ✅ Generated: ${filename}`);
    }

    console.log('\n✅ Dynamic title sizing test completed successfully');
  } catch (error) {
    console.error('❌ Dynamic title sizing test failed:', error);
  }
}

/**
 * Generate Word document with template and return as Blob instead of downloading
 */
export async function generateWordDocumentBlob(
  data: TemplateData,
  options: TemplateExportOptions = {}
): Promise<Blob> {
  try {
    console.log('🚀 Starting template-based Word document generation (blob)');
    console.log('📊 Export data:', {
      title: data.title,
      hasContent: !!data.content,
      hasToc: !!data.tocContent,
      contentLength: data.content?.length || 0,
      tocLength: data.tocContent?.length || 0,
    });

    // Validate required data
    if (!data.title) {
      throw new TemplateExportError('Document title is required');
    }

    // Load the template
    const templatePath =
      options.templatePath || '/letter-head/letterhead-template.docx';
    const templateBuffer = await loadTemplate(templatePath);

    // Add content to template
    const documentBuffer = await addContentToTemplate(templateBuffer, data);

    // Create blob without downloading
    const blob = new Blob([documentBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    });

    console.log('✅ Template-based Word document blob created successfully');
    return blob;
  } catch (error) {
    console.error(
      '❌ Template-based Word document blob generation failed:',
      error
    );
    if (error instanceof TemplateExportError) {
      throw error;
    }
    throw new TemplateExportError(
      'Blob generation failed',
      error instanceof Error ? error.message : 'Unknown error'
    );
  }
}

/**
 * Export Word document with template directly to PDF using the backend conversion service
 */
export async function exportWordTemplateToPdf(
  data: TemplateData,
  options: TemplateExportOptions = {}
): Promise<void> {
  try {
    console.log('🚀 Starting template-based Word-to-PDF export');

    // Generate the Word document as blob
    const wordBlob = await generateWordDocumentBlob(data, options);

    // Import the PDF conversion service
    const { uploadDocAndGetPdf } = await import('./pdfConversionService');

    // Generate filename for the conversion
    const filename = generateFilename(data.title, options.filename);
    const wordFilename = filename.endsWith('.docx')
      ? filename
      : `${filename}.docx`;

    console.log('🔄 Converting Word document to PDF via backend service');

    // Convert to PDF using the backend service
    await uploadDocAndGetPdf(wordBlob, wordFilename, {}, true);

    console.log('✅ Template-based Word-to-PDF export completed successfully');
  } catch (error) {
    console.error('❌ Template-based Word-to-PDF export failed:', error);
    if (error instanceof TemplateExportError) {
      throw error;
    }
    throw new TemplateExportError(
      'Word-to-PDF export failed',
      error instanceof Error ? error.message : 'Unknown error'
    );
  }
}

// Make functions available globally for debugging
if (typeof window !== 'undefined') {
  (window as any).analyzeTemplate = analyzeTemplate;
  (window as any).testTemplate = testTemplate;
  (window as any).analyzeTitleTemplate = analyzeTitleTemplate;
  (window as any).testDynamicTitleSizing = testDynamicTitleSizing;
  (window as any).generateDynamicTitleContent = generateDynamicTitleContent;
}
